/**
 *
 * ChannelList
 *
 */

import React, { Component } from 'react';
import { Link } from 'react-router-dom'
import withRouter from 'withRouter'
import { getIn } from 'utils/helpers';
import { connect } from 'react-redux';
import { PLAN_SELECTION } from 'containers/RootModal/constants';
import { INTUITIVE_MODAL } from 'containers/RootModal/constants';
import { RESUBSCRIBE_MODAL } from 'containers/RootModal/constants';
import {
  Wrapper,
  Title,
  StatusFilterList,
  StatusFilterOption,
  StatusFilterOptionItem,
  RowHyperlink,
  TableData,
  StyledFilter,
  FilterButton
} from './styledComponents';
import Button from '../../../components/Button';
import EmptyList from './EmptyList';
import H1 from 'components/H1';
import ListError from 'components/ListError';
import Spinner from 'components/Spinner'
import { setModal } from 'containers/RootModal/actions';
import { deleteChannelRequest, archiveChannelRequest, setClientFilter, setStatusFilter, fetchChannelsRequest } from '../actions';
import ChannelActionsMenu from './ChannelActionsMenu';
import Table from "components/Table";
import Icon from 'components/Icon';
import { selectAllChannels, selectStatusFilter, selectClientFilter, selectIsFetching, selectIsLoaded, selectListError, selectClients } from '../selectors';
import { selectIsPhotographer, selectChannelManagementBlocked } from '../../App/selectors';
import { createStructuredSelector } from 'reselect';

const columns = [
  {
    header: 'name',
    path: 'name',
    noSort: true,
    customRow: (rowData) => rowData.name
  },
  {
    header: 'account',
    path: 'clientName',
    noSort: true,
    customRow: (rowData) => rowData.clientName
  },
  {
    header: 'gallery',
    path: 'url',
    noSort: true,
    width: '110px',
    customRow: (rowData) => {
      const slug = rowData.slug || (rowData.url ? rowData.url.split("/")[3] : null);
      return slug ? (
        <RowHyperlink href={`/ch/${slug}`} target="_blank" rel="noopener noreferrer">view gallery</RowHyperlink>
      ) : null;
    }
  },
  {
    header: 'last updated',
    path: 'updated_at',
    type: 'date',
    width: '120px'
  },
  {
    header: '',
    type: "dropdown",
    width: '50px',
    customRow: ({ permissionRole, name, id, url, slug, can_delete, can_archive, deleteChannel, archiveChannel, channelManagementBlocked, openResubscribeModal }) => (
      <ChannelActionsMenu
        id={id}
        name={name}
        url={url}
        slug={slug}
        permissionRole={permissionRole}
        canDelete={can_delete}
        canArchive={can_archive}
        deleteChannel={deleteChannel}
        archiveChannel={archiveChannel}
        channelManagementBlocked={channelManagementBlocked}
        openResubscribeModal={openResubscribeModal}
      />
    )
  },
]

const getData = ({ channels, permissionRole, deleteChannel, archiveChannel, changeClientFilter, channelManagementBlocked, openResubscribeModal }) => {
  return channels ? channels.map(c => (
    {
      ...c,
      clientId: c.client && c.client.id,
      clientName: c.client && c.client.name,
      slug: c.slug || (c.url ? c.url.split("/")[3] : null),
      permissionRole,
      deleteChannel,
      archiveChannel,
      changeClientFilter,
      channelManagementBlocked,
      openResubscribeModal
    }
  )) : null;
}

export class ChannelList extends Component {
  state = {
    sort: { path: 'updated_at', isDesc: true },
    page: 1
  }

  componentDidMount() {
    this.props.fetchChannels({ ...this.getFilter(), page: 1 });
  }

  componentDidUpdate(prevProps) {
    const { statusFilter, clientFilter, fetchChannels } = this.props;
    if (prevProps.statusFilter !== statusFilter || JSON.stringify(prevProps.clientFilter) !== JSON.stringify(clientFilter)) {
      fetchChannels({ ...this.getFilter(), status: statusFilter, client_id: clientFilter ? clientFilter.id : null, page: 1 })
    }
  }

  handlePageChange = (page) => {
    this.props.fetchChannels({ ...this.getFilter(), page });
  }

  // Re-issue the request that failed, on the filters still on screen. `paging`
  // is `{}` after a failure — nothing populated it — so fall back to page 1
  // rather than sending `page=undefined`.
  handleRetry = () => {
    this.props.fetchChannels({ ...this.getFilter(), page: this.props.paging.currentPage || 1 });
  }

  handleSortChanged = (sortObj) => {
    this.setState({ sort: sortObj }, () => {
      this.props.fetchChannels({ ...this.getFilter(), page: 1 });
    });
  }

  getFilter = () => {
    return {
      page: this.props.paging.currentPage,
      status: this.props.statusFilter,
      // `this.props`, not `this.state`. ChannelList keeps no `clientFilter` in
      // state — only `sort` and `page` — so this read was always undefined and
      // every fetch derived from getFilter sent `client_id: null`, silently
      // dropping the client filter the header was still showing as applied.
      // Only componentDidUpdate got it right, because it passes `client_id`
      // explicitly; that is why CHANGING the filter worked and paging, sorting
      // or retrying under it did not. Same defect as LeadsList's route scope:
      // a refetch must reissue the filter that is on screen.
      client_id: this.props.clientFilter ? this.props.clientFilter.id : null,
      sort_name: this.state.sort.path,
      sort_direction: this.state.sort.isDesc ? 'desc' : 'asc',
    };
  }

  // Clicking a row IS "edit this channel" — it is the same destination the ⋯
  // menu's "edit channel" reaches, so it has to be gated the same way. Left
  // ungated it would be the one route into the editor a lapsed account could
  // still take, and the most-used one.
  // `can_archive` is the per-channel EDIT verdict, under a legacy name. The
  // rails ChannelMapper computes it from `ChannelPolicy#update?` — archiving is
  // a channel UPDATE (`PATCH /api/v2/channels/{id}` with `archived: true`), so
  // the flag has always mirrored update access, and the subscription gate was
  // added inside that same policy. It therefore already carries the exemption
  // the account-wide flag cannot express: a channel_admin may edit channels
  // they administer for a client even when their OWN subscription has ended.
  //
  // So the account-wide flag only opens the question, and the per-channel
  // verdict answers it. Strict `!== true`: an absent flag on a blocked account
  // stays blocked. An entitled account never reaches this expression at all,
  // because the outer flag is false.
  handleRowClick = (rowData) => {
    if (this.props.channelManagementBlocked && rowData.can_archive !== true) {
      this.props.openResubscribeModal();
      return;
    }
    this.props.navigate(`/channels/${rowData.id}/edit`);
  }

  render() {
    const {
      clients,
      statusFilter,
      changeStatusFilter,
      clearClientFilter,
      clientFilter,
      isFetching,
      loaded,
      listError,
      isPhotographer,
      remainingLiteChannel,
      remainingTrialChannel,
      ifUnlimitiedPhotographer,
      openPasswordRequirementsModal,
      openIntuitiveModal,
      ifLitePhotographer,
      subscriptionCurrentStatus,
      remaingDaysInRenewel,
      paging,
      channelManagementBlocked,
      openResubscribeModal,
    } = this.props;
    const data = getData(this.props);
    const isEmpty = data && data.length > 0 ? false : true;
    // A failed fetch leaves no data, so it is `isEmpty` too — which is exactly
    // how it used to end up rendering the empty state. Everything below that
    // branches on emptiness has to ask this first, including the header's
    // "New Channel" button: inviting someone to create a channel is wrong when
    // we could not reach the server to tell them what they already have.
    const hasFailed = Boolean(listError);
    const tableProps = { isFetching, data, columns, ...paging, onPageChange: this.handlePageChange, sortChanged: this.handleSortChanged, onRowClick: this.handleRowClick };
    const newChannelOnClick = () => {
      (!remainingLiteChannel && ifLitePhotographer && subscriptionCurrentStatus)
        ? openIntuitiveModal(remaingDaysInRenewel)
        : openPasswordRequirementsModal()
    }
    return (
      <Wrapper>
        <Title>
          <H1>Channels</H1>
          { // NOTE: Could be mapped over if multiple filters can be added
            clientFilter && !isFetching && (
              <StyledFilter>
                <FilterButton type="button" aria-label="Clear client filter" onClick={() => clearClientFilter()}><Icon size='8' name='close' /></FilterButton>
                {` Client: ${clientFilter.name}`}
              </StyledFilter>
            )
          }
          {/*
            The resubscribe branch is asked FIRST. The two branches below it ask
            what a photographer's plan entitles them to; this one asks whether
            the account still has a subscription at all, which is the earlier
            question — a lapsed photographer satisfies a plan branch too, and
            answering that one would send them to a prompt about the wrong
            problem. Rendering a Button (not a Link) is the point: there is no
            entitled destination to link to.
          */}
          {!isEmpty && !hasFailed && (
            channelManagementBlocked
              ? <Button onClick={openResubscribeModal}>New Channel</Button>
              : isPhotographer && !(remainingLiteChannel || remainingTrialChannel || ifUnlimitiedPhotographer || ifLitePhotographer)
                ? <Button onClick={newChannelOnClick}>New Channel</Button>
                : <Link to='/channels/new'><Button>New Channel</Button></Link>
          )}
        </Title>
        <StatusFilterList>
          {
            ['active', 'archived'].map((f) => {
              const label = f.charAt(0).toUpperCase() + f.slice(1);
              return (
              <StatusFilterOption
                key={f}
                type="button"
                className={f === statusFilter ? 'active' : null}
                aria-pressed={f === statusFilter}
                onClick={() => changeStatusFilter(f)}
              >
                <StatusFilterOptionItem isActive={f === statusFilter}>
                  {label}
                </StatusFilterOptionItem>
              </StatusFilterOption>
              );
            })}
        </StatusFilterList>
        {/*
          Keyed on `loaded` — has the list request settled — rather than on
          `isFetching`. `isFetching && !data` left a window on mount, before
          componentDidMount's fetch was dispatched, in which neither was set
          and the user was told "It's looking empty around here…" about a list
          nobody had asked the server for yet. That flash is also what made the
          empty state useless as a signal to the e2e suite, which cannot tell a
          list that resolved to nothing from one that has not been requested.
          A refetch (paging, sorting) keeps `loaded` true and stays on the
          table, which passes `isFetching` for its own in-table indicator.
        */}
        {/*
          Three conditions, three states. `hasFailed` has to be asked BEFORE
          `isEmpty`, because a failed fetch has no rows either — routing it into
          the empty state is what told a user whose list 500'd that they had no
          channels, and what let an e2e guard `test.skip()` on a server error.
        */}
        {!loaded
          ? <Spinner />
          : hasFailed
            ? <ListError title="We couldn’t load your channels" onRetry={this.handleRetry} isRetrying={isFetching} />
            : (isEmpty
              ? <EmptyList blocked={channelManagementBlocked} openResubscribeModal={openResubscribeModal} />
              : <Table {...tableProps} />)
        }
      </Wrapper>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  channels: selectAllChannels(),
  statusFilter: selectStatusFilter(),
  clientFilter: selectClientFilter(),
  isFetching: selectIsFetching(),
  loaded: selectIsLoaded(),
  listError: selectListError(),
  clients: selectClients(),
  isPhotographer: selectIsPhotographer(),
  channelManagementBlocked: selectChannelManagementBlocked(),
  remainingLiteChannel: state => state.app.user.remaining_lite_channel,
  remainingTrialChannel: state => state.app.user.remaining_trial_channel,
  ifUnlimitiedPhotographer: state => state.app.user.if_unlimitied_photographer,
  ifLitePhotographer: state => state.app.user.if_lite_photographer,
  subscriptionCurrentStatus: state => state.app.user.subscription_current_status,
  remaingDaysInRenewel: state => state.app.user.remaining_days_in_renewel,
  permissionRole: state => state.app.user.permission_role,
  paging: state => state.channels.paging,
});

;
const mapDispatchToProps = (dispatch, _ownProps) => {
  return {
    openPasswordRequirementsModal: () => dispatch(setModal({ modalType: PLAN_SELECTION })),
    openIntuitiveModal: (days) => dispatch(setModal({ modalType: INTUITIVE_MODAL, modalProps: { remainingDays: days } })),
    openResubscribeModal: () => dispatch(setModal({ modalType: RESUBSCRIBE_MODAL })),
    deleteChannel: (id) => dispatch(deleteChannelRequest(id)),
    archiveChannel: (id) => dispatch(archiveChannelRequest(id)),
    changeClientFilter: (val) => dispatch(setClientFilter(val)),
    clearClientFilter: () => dispatch(setClientFilter('')),
    changeStatusFilter: (val) => dispatch(setStatusFilter(val)),
    fetchChannels: (params) => dispatch(fetchChannelsRequest(params)),
  }
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ChannelList));
